import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// ================ Action types ================ //

export const FETCH_BOOKINGS_REQUEST = 'app/SchedulerPage/FETCH_BOOKINGS_REQUEST';
export const FETCH_BOOKINGS_SUCCESS = 'app/SchedulerPage/FETCH_BOOKINGS_SUCCESS';
export const FETCH_BOOKINGS_ERROR = 'app/SchedulerPage/FETCH_BOOKINGS_ERROR';

export const MAKE_TRANSITION_REQUEST = 'app/SchedulerPage/MAKE_TRANSITION_REQUEST';
export const MAKE_TRANSITION_SUCCESS = 'app/SchedulerPage/MAKE_TRANSITION_SUCCESS';
export const MAKE_TRANSITION_ERROR = 'app/SchedulerPage/MAKE_TRANSITION_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,
  fetchBookingsError: null,
  bookings: [],
  transactions: [],
  transitionInProgress: null,
  transitionError: null,
};

export default function schedulerPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_BOOKINGS_REQUEST:
      return { ...state, fetchInProgress: true, fetchBookingsError: null };
    case FETCH_BOOKINGS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        bookings: payload.bookings,
        transactions: payload.transactions,
      };
    case FETCH_BOOKINGS_ERROR:
      return { ...state, fetchInProgress: false, fetchBookingsError: payload };
    
    case MAKE_TRANSITION_REQUEST:
      return { ...state, transitionInProgress: payload, transitionError: null };
    case MAKE_TRANSITION_SUCCESS: {
      // Update the transaction in the transactions array
      const updatedTransaction = payload.data;
      const updatedTransactions = state.transactions.map(tx =>
        tx.id.uuid === updatedTransaction.id.uuid ? updatedTransaction : tx
      );
      return {
        ...state,
        transitionInProgress: null,
        transactions: updatedTransactions,
      };
    }
    case MAKE_TRANSITION_ERROR:
      return { ...state, transitionInProgress: null, transitionError: payload };
    
    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchBookingsRequest = () => ({ type: FETCH_BOOKINGS_REQUEST });
const fetchBookingsSuccess = (bookings, transactions) => ({
  type: FETCH_BOOKINGS_SUCCESS,
  payload: { bookings, transactions },
});
const fetchBookingsError = e => ({
  type: FETCH_BOOKINGS_ERROR,
  error: true,
  payload: e,
});

const makeTransitionRequest = transitionName => ({
  type: MAKE_TRANSITION_REQUEST,
  payload: transitionName,
});

const makeTransitionSuccess = response => ({
  type: MAKE_TRANSITION_SUCCESS,
  payload: response.data,
});

const makeTransitionError = e => ({
  type: MAKE_TRANSITION_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const loadData = () => (dispatch, getState, sdk) => {
  dispatch(fetchBookingsRequest());

  // Query transactions as sales (provider view) to get bookings.
  // NOTE: do NOT pass lastTransitions here. Listing every transition of every
  // process produced a ~10KB query URL that the Marketplace API rejected before
  // returning CORS headers, surfacing in the browser as a CORS / net::ERR_FAILED
  // failure ("Failed to load bookings"). Omitting it returns all sales, which the
  // component already filters (declined txs are skipped in scheduleData).
  return sdk.transactions
    .query({
      only: 'sale',
      include: ['listing', 'booking', 'provider', 'customer'],
      'fields.transaction': ['processName', 'lastTransition', 'lastTransitionedAt', 'protectedData'],
      'fields.listing': ['title', 'publicData'],
      perPage: 100,
    })
    .then(response => {
      const transactions = response.data.data;
      const included = response.data.included || [];
      
      // Extract bookings from included data
      const bookings = included.filter(item => item.type === 'booking');
      
      // Add marketplace entities - pass the whole response, not response.data
      dispatch(addMarketplaceEntities(response));
      
      dispatch(fetchBookingsSuccess(bookings, transactions));
      return response;
    })
    .catch(e => {
      dispatch(fetchBookingsError(storableError(e)));
      throw e;
    });
};

export const fetchBookings = loadData;

// Make transition (accept, decline, etc.)
export const makeTransition = (transactionId, transitionName, params) => (dispatch, getState, sdk) => {
  dispatch(makeTransitionRequest(transitionName));

  return sdk.transactions
    .transition({
      id: transactionId,
      transition: transitionName,
      params: params || {},
    })
    .then(response => {
      // Add marketplace entities from response
      dispatch(addMarketplaceEntities(response));
      dispatch(makeTransitionSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(makeTransitionError(storableError(e)));
      throw e;
    });
};
